import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useRef } from 'react';
import { useSearchParams } from '@remix-run/react';
import { getServerConfig } from '../services/config';
export const BlogContext = createContext({});
export const BlogProvider = BlogContext.Provider;
export const BlogConsumer = BlogContext.Consumer;
export default function LosseBlogBink({ children, pageInfo, data, headerScrollOffset = 0, ...rest }) {
    var _a;
    const ref = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const activeFilters = (searchParams.get('_f') ? (_a = searchParams.get('_f')) === null || _a === void 0 ? void 0 : _a.split(',') : []);
    const isFilterActive = (databaseId) => databaseId && activeFilters.includes(databaseId.toString());
    const search = (params, shouldScroll = false) => {
        setSearchParams(params || {}, {
            replace: true,
            state: {
                scroll: false
            }
        });
        if (shouldScroll && ref.current) {
            const offset = ref.current.offsetTop;
            window.scrollTo({ top: offset - headerScrollOffset });
        }
    };
    const nextPage = () => {
        searchParams.set('_c', (pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.endCursor) || '');
        searchParams.set('_d', 'a');
        search(searchParams, true);
    };
    const prevPage = () => {
        searchParams.set('_c', (pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.startCursor) || '');
        searchParams.set('_d', 'b');
        search(searchParams, true);
    };
    const setOrder = (orderBy) => {
        searchParams.set('_s', orderBy);
        search(searchParams);
    };
    const clear = () => {
        search();
    };
    const toggleFilter = (databaseId) => {
        if (!databaseId) {
            return;
        }
        const _databaseId = databaseId.toString();
        let _filters = [...activeFilters];
        if (_filters.includes(_databaseId)) {
            _filters = _filters.filter((id) => id !== _databaseId);
        }
        else {
            _filters = [..._filters, _databaseId];
        }
        searchParams.set('_f', _filters.join(','));
        search(searchParams);
    };
    return (_jsx(BlogProvider, { value: {
            toggleFilter,
            isFilterActive,
            setOrder,
            nextPage,
            hasNextPage: (pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.hasNextPage) || false,
            prevPage,
            hasPrevPage: (pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.hasPreviousPage) || false,
            clear,
            data
        }, children: _jsx("form", { ref: ref, method: "get", action: "", ...rest, children: children }) }));
}
// Somehow URL params can end up like this: ?_f=109%3F_f%3D109 (double ?_f=) and break everything
const decodeAndReplace = (params, find = '?_f=') => (params ? decodeURIComponent(params).replace(find, ',') : '');
export const losseBlogBinkPrepare = ({ url, limit = 9, defaultOrder = 'ASC', defaultOrderBy = 'DATE' }) => {
    var _a;
    const cursor = url.searchParams.get('_c') || '';
    const direction = url.searchParams.get('_d') || '';
    const filters = decodeAndReplace(url.searchParams.get('_f'));
    const sort = url.searchParams.get('_s') || '';
    const query = url.searchParams.get('_q') || '';
    const sortSplit = sort.split('_');
    let orderBy = defaultOrderBy;
    let order = defaultOrder;
    if (sortSplit.length > 1) {
        orderBy = sortSplit[0];
        order = sortSplit[1];
    }
    let condition = `, first: ${limit}`;
    let where = ``;
    if (direction === 'a' && cursor) {
        condition = `, first: ${limit}, after: "${cursor}"`;
    }
    if (direction === 'b' && cursor) {
        condition = `, last: ${limit}, before: "${cursor}"`;
    }
    if (query) {
        where = `, search: "${query}"`;
    }
    if (filters) {
        if ((_a = getServerConfig().features) === null || _a === void 0 ? void 0 : _a.includes('categoryIds')) {
            where = `, categoryIds: [${filters}]`;
        }
        else {
            where = `, categoryIn: [${filters}]`;
        }
    }
    return { cursor, direction, filters, sort, query, orderBy: orderBy.toUpperCase(), order: order.toUpperCase(), where, condition };
};
export const losseBlogBinkFragment = ({ fields, hasTotal = false, labels, ...rest }) => {
    const { orderBy, order, where, condition } = losseBlogBinkPrepare(rest);
    return `
    ${labels.filters
        ? `${labels.filters} {
          databaseId
          name
          parentDatabaseId
        }`
        : ''}
    ${labels.posts}(where: {orderby: {field: ${orderBy}, order: ${order}}, status: PUBLISH${where}}${condition}) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
        ${hasTotal ? 'total' : ''}
      }
      edges {
        node {
          id
          databaseId
          uri
          ${fields}
        }
      }
    }
  `;
};
